import * as THREE from 'three'
import { RIM_LIGHTS, HEMI, AMBIENT } from './sceneLights'

/**
 * 建筑详情三维视图：把选中的那栋楼"搬"进驾驶舱面板里单独转着看。
 *
 * 为什么不用第二个 WebGLRenderer：新上下文要重新上传一遍这栋楼的显存，
 * 而且浏览器对同时存在的 WebGL 上下文有硬上限（切几次面板就掉上下文了）。
 * 这里复用主渲染器，靠 scissor + viewport 在画布的某一块矩形上再画一遍
 * —— 几何体与主场景共享同一份 buffer，零拷贝。
 *
 * ⚠️ 坐标系：主场景是 Z-up（X=东 / Y=北 / Z=上，见 sceneLights.js 顶部那段），
 * 本视图沿用同一套，不另外旋转，否则克隆出来的世界矩阵会整体歪掉。
 */

/** 归一化后模型最长边（含水平对角）占的单位长度，相机按它取景 */
const FIT_SPAN = 2.6
/** 详情相机视场：比主相机窄，透视畸变小，看着像"产品图" */
const CAM_FOV = 26
/** 展示机位的方位角（弧度）。详情相机默认在模型南侧朝北看，即 π */
const PRESENT_AZ = Math.PI
/**
 * 入场已改为"直接切换"：换楼时模型立即以展示机位出现在窗口正中，
 * 不再等主相机飞行（hold），也不再从屏外侧滑进屏（slide）—— 切换的过渡感
 * 由面板自己的扫描光带（CockpitFrame 的 .ck-viewer.is-switching）承担。
 * 历史方案（两段式：hold 440ms + 模型从屏外滑入 760ms）已随需求移除；
 * ⚠️ 无论如何都别改成"让整个矩形从地图上的楼那里飞过来"：矩形画在画布上，
 *    一路缩过来会把地图盖成一块飘着的大半透明方片（实测被否）。
 */
/** 自转角速度（弧度/秒） */
const SPIN_RAD_PER_SEC = 0.4
/** 拖拽灵敏度（弧度 / 像素）：横向转方位、纵向改俯仰 */
const ORBIT_AZ_PER_PX = 0.0075
const ORBIT_EL_PER_PX = 0.0035
/** 详情相机俯仰：默认值与可拖到的上下限（弧度）。下限不给到 0，纯侧视会把楼压成一条线 */
const CAM_ELEV = 0.3
const CAM_ELEV_MIN = 0.1
const CAM_ELEV_MAX = 1.15
/** 松手后的惯性衰减（1/秒）与"停多久才恢复自转"（毫秒） */
const ORBIT_DAMP = 3.4
const SPIN_RESUME_MS = 900
/** 详情视口底色（面板玻璃同色系，别让模型漂在透明上） */
const VIEWPORT_BG = 0x0a1322

/** 径向渐变贴图，给视口底下一圈"展台光" */
function glowTexture() {
  const size = 128
  const cv = document.createElement('canvas')
  cv.width = cv.height = size
  const ctx = cv.getContext('2d')
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
  g.addColorStop(0, 'rgba(122,168,255,0.55)')
  g.addColorStop(0.55, 'rgba(70,104,170,0.22)')
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  const tex = new THREE.CanvasTexture(cv)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

/**
 * @param {THREE.WebGLRenderer} renderer 主渲染器（本模块只借用，不改它的尺寸）
 */
export function createBuildingDetailView({ renderer }) {
  const scene = new THREE.Scene()

  // 相机要在场景里，它挂的视口底板才会被渲染
  const camera = new THREE.PerspectiveCamera(CAM_FOV, 1, 0.02, 400)
  camera.up.set(0, 0, 1)
  camera.name = 'detailCamera'
  scene.add(camera)

  // 视口底板：挂在相机前方法线方向上，永远铺满这个 scissor 矩形。
  // 透明度由入场进度驱动 —— 飞入途中是"镂空"的（只看见楼，像从场景里抠出来的一块），
  // 落位后才慢慢铺上底色，变成面板里的一个视口。
  const backdrop = new THREE.Mesh(
    new THREE.PlaneGeometry(1, 1),
    new THREE.MeshBasicMaterial({
      color: VIEWPORT_BG,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      toneMapped: false,
    }),
  )
  backdrop.renderOrder = -10
  camera.add(backdrop)

  const ambient = new THREE.AmbientLight(AMBIENT.color, AMBIENT.intensity * 1.35)
  const hemi = new THREE.HemisphereLight(HEMI.sky, HEMI.ground, HEMI.intensity * 1.25)
  hemi.position.set(0, 0, 1)
  scene.add(ambient, hemi)
  // 沿用主场景那三盏 rim 光的方位，模型明暗和场景里看的是同一套，
  // 只是强度抬高：小视口里没有环境反射，不补就发灰
  const rims = RIM_LIGHTS.map((cfg) => {
    const l = new THREE.DirectionalLight(cfg.color, cfg.intensity * 1.15)
    const a = (cfg.az * Math.PI) / 180
    const e = (cfg.el * Math.PI) / 180
    const horiz = Math.cos(e)
    l.position.set(horiz * Math.sin(a), horiz * Math.cos(a), Math.sin(e))
    return l
  })
  scene.add(...rims)

  /** 自转轴：模型已居中到原点，绕 Z（=上）转就是绕楼的中轴转 */
  const spin = new THREE.Group()
  spin.name = 'detailSpin'
  scene.add(spin)

  // 展台：一圈渐变光 + 一道细环，给"悬浮在面板里"一个落点
  const stage = new THREE.Group()
  const glow = new THREE.Mesh(
    new THREE.CircleGeometry(FIT_SPAN * 1.15, 48),
    new THREE.MeshBasicMaterial({
      map: glowTexture(),
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      toneMapped: false,
    }),
  )
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(FIT_SPAN * 0.62, FIT_SPAN * 0.645, 64),
    new THREE.MeshBasicMaterial({
      color: 0x7fd6ff,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      toneMapped: false,
    }),
  )
  stage.add(glow, ring)
  spin.add(stage)

  let model = null // 当前这栋楼的克隆根节点（挂在 spin 上）
  let modelSpan = FIT_SPAN
  let modelHeight = FIT_SPAN
  let slotEl = null
  let disposed = false

  // ---- 入场 ----
  // 方位角**全程锁死成主相机那一个**：模型露出来的正好是用户在地图里看到的那一面。
  let camAz = PRESENT_AZ
  let lastAt = 0

  // ---- 拖拽轨道 ----
  // 方位角只用一个累加值：自转、拖拽、惯性都往 angle 上加，避免三套角度再互相换算
  let angle = 0
  let elevOffset = 0 // 相对 CAM_ELEV 的俯仰偏移
  let orbitVel = 0 // 惯性角速度（弧度/秒）
  let dragging = false
  let lastMoveAt = 0

  const clampElev = (v) => (v < CAM_ELEV_MIN ? CAM_ELEV_MIN : v > CAM_ELEV_MAX ? CAM_ELEV_MAX : v)

  function beginOrbit() {
    dragging = true
    orbitVel = 0
    lastAt = performance.now()
  }

  /** 拖拽位移（CSS 像素）。dy 向下为正 = 相机往下压 */
  function orbitBy(dx, dy) {
    if (!model) return false
    const now = performance.now()
    const dt = lastAt ? Math.min(0.1, (now - lastAt) / 1000) : 0.016
    lastAt = now
    const dAz = dx * ORBIT_AZ_PER_PX
    angle += dAz
    elevOffset = clampElev(CAM_ELEV + elevOffset - dy * ORBIT_EL_PER_PX) - CAM_ELEV
    if (dt > 0.001) orbitVel = dAz / dt
    lastMoveAt = now
    return true
  }

  function endOrbit() {
    dragging = false
    lastAt = performance.now()
  }

  function disposeModel() {
    if (!model) return
    // 只摘节点，不 dispose 任何东西：geometry 和 material 都是从主场景借来的，
    // 在这里释放会把场景里那栋楼一起毁掉
    spin.remove(model)
    model = null
  }

  /**
   * 装一栋楼进视图（直接切换，无入场动画，过渡感由面板的扫描光带承担）。
   * @param {{pieces:{geometry:THREE.BufferGeometry, material:THREE.Material, matrix:THREE.Matrix4}[], box:THREE.Box3}} src
   *        sceneView 从 controls.buildings.sourceOf(code) 拿到的真实世界位形
   * @param {{fromAzimuthDeg?:number}} [opts]
   *        fromAzimuthDeg = 主相机此刻的方位角，详情相机沿用同一方位，
   *        面板里露出来的才是用户在地图里看到的那一面。
   */
  function show(src, opts) {
    const o = opts || {}
    disposeModel()
    if (!src || !src.pieces || !src.pieces.length || !src.box) return false

    // 逐块抄世界矩阵当局部矩阵、父级给单位阵：拼出来就是它在世界里的样子，
    // 一块楼的几何散在多个 GLB / 多个父节点时不会错位
    // 材质**直接用主场景那一份**：克隆一份出来要为每个新材质再编译一遍着色器程序
    // （实测第一次打开详情视图 451ms，第二次 88ms，全是这个）。详情视图的灯组数量与
    // 主场景一致（ambient + hemi + 3 rim），共用程序缓存；而主场景的 mesh 材质引用
    // 会被选中态整体换掉（不是原地改），所以这里共享原始材质不会被全息效果污染。
    const root = new THREE.Group()
    root.matrixAutoUpdate = false
    src.pieces.forEach((pc) => {
      const m = new THREE.Mesh(pc.geometry, pc.material)
      m.matrixAutoUpdate = false
      m.matrix.copy(pc.matrix)
      m.matrixWorldNeedsUpdate = true
      m.castShadow = false
      m.receiveShadow = false
      root.add(m)
    })

    const size = src.box.getSize(new THREE.Vector3())
    const center = src.box.getCenter(new THREE.Vector3())
    // 水平按对角、竖直按楼高取大者：长方形楼按单边缩放会顶出视口
    const span = Math.max(Math.hypot(size.x, size.y), size.z) || 1
    const k = FIT_SPAN / span
    // 底面贴 z=0（展台所在平面），不是几何中心 —— 楼要"站在"光晕上
    const shift = new THREE.Vector3(-center.x * k, -center.y * k, -src.box.min.z * k)

    const fit = new THREE.Group()
    fit.matrixAutoUpdate = false
    fit.matrix = new THREE.Matrix4().makeScale(k, k, k).premultiply(new THREE.Matrix4().makeTranslation(shift.x, shift.y, shift.z))
    fit.matrixWorldNeedsUpdate = true
    fit.add(root)

    model = fit
    spin.add(model)
    modelSpan = FIT_SPAN
    modelHeight = Math.min(size.z * k, FIT_SPAN * 1.6)

    // ---- 机位状态 ----
    camAz = Number.isFinite(o.fromAzimuthDeg) ? (o.fromAzimuthDeg * Math.PI) / 180 : PRESENT_AZ
    lastAt = 0
    // 换楼要回到默认机位，否则上一栋拖出来的俯仰与自转会带过来
    angle = 0
    elevOffset = 0
    orbitVel = 0
    dragging = false
    spin.rotation.z = 0
    spin.position.set(0, 0, 0)
    spin.scale.setScalar(1)
    return true
  }

  function hide() {
    disposeModel()
    slotEl = null
  }

  /** 面板里的目标窗口（DOM 元素），每帧量一次，收起/滚动都能跟上 */
  function setSlot(el) {
    slotEl = el || null
  }

  function fitDistance(aspect) {
    // 分别按"竖直要装下多少"和"水平要装下多少"算距离，取远的。
    // 只按包围球/对角线取景的话，又扁又宽的楼（占地 150m、高 100m 这类）会在
    // 窗口上下各留一大片空白 —— 实测 14 号楼只占到窗口高度的四成。
    const halfV = Math.max(modelHeight * 0.62, modelSpan * 0.16)
    const halfH = modelSpan * 0.55
    const tanV = Math.tan((CAM_FOV * Math.PI) / 360)
    const tanH = Math.tan((CAM_FOV * Math.PI) / 360) * Math.max(0.25, aspect)
    return Math.max(halfV / tanV, halfH / tanH) * 1.08
  }

  /** 在 composer 出图之后调用：把详情视图画进面板槽位那块矩形 */
  function render() {
    if (disposed || !model || !slotEl || !slotEl.isConnected) return false
    const canvas = renderer.domElement
    const cr = canvas.getBoundingClientRect()
    const sr = slotEl.getBoundingClientRect()
    const w = Math.round(sr.width)
    const h = Math.round(sr.height)
    if (w < 8 || h < 8 || cr.width < 8) return false

    const now = performance.now()
    const dt = lastAt ? Math.min(0.1, (now - lastAt) / 1000) : 0
    lastAt = now

    // 自转 / 拖拽惯性（换楼时机位已在 show() 里归零，模型即刻出现在窗口正中）
    if (dragging) {
      // angle 由 orbitBy 实时累加
    } else if (Math.abs(orbitVel) > 0.02) {
      angle += orbitVel * dt
      orbitVel *= Math.exp(-ORBIT_DAMP * dt)
      if (Math.abs(orbitVel) <= 0.02) orbitVel = 0
    } else if (now - lastMoveAt > SPIN_RESUME_MS) {
      angle += SPIN_RAD_PER_SEC * dt
    }
    spin.rotation.z = angle

    const rect = { x: sr.left - cr.left, y: sr.top - cr.top, w, h }

    // ⚠️ setViewport / setScissor 吃的是 **CSS 单位**，three 内部自己乘 pixelRatio
    // （`_currentViewport.copy(_viewport).multiplyScalar(_pixelRatio).floor()`）。
    // 这里再乘一次，dpr≠1 的机器上窗口就会成倍放大错位。
    const vx = Math.max(0, Math.round(rect.x))
    const vw = Math.max(1, Math.round(rect.w))
    // three 的 viewport 原点在左下，DOM 的在左上
    const vy = Math.max(0, Math.round(cr.height - rect.y - rect.h))
    const vh = Math.max(1, Math.round(rect.h))

    const aspect = rect.w / rect.h
    // 方位全程锁死成主相机那一个（见 camAz 注释）；俯仰 = 展示机位 + 拖拽偏移
    const az = camAz
    const elev = clampElev(CAM_ELEV + elevOffset)
    // 俯视越深，楼的水平投影占的竖直空间越大，距离要跟着让一点，否则屋顶会被裁
    const dist = fitDistance(aspect) * (1 + (elev - CAM_ELEV) * 0.22)
    const horiz = dist * Math.cos(elev)
    camera.aspect = aspect
    camera.position.set(horiz * Math.sin(az), horiz * Math.cos(az), dist * Math.sin(elev))
    camera.lookAt(0, 0, modelHeight * 0.5)
    camera.updateProjectionMatrix()
    // 底板铺满相机视锥的远端；屏底颜色恒定（切换的过渡感由面板扫描光带承担）
    const bh = 2 * Math.tan((CAM_FOV * Math.PI) / 360) * dist * 2
    backdrop.scale.set(bh * Math.max(1, aspect) * 1.2, bh * 1.2, 1)
    backdrop.position.set(0, 0, -dist * 2)
    backdrop.material.opacity = 0.92
    glow.material.opacity = 0.9
    ring.material.opacity = 0.5

    renderer.setRenderTarget(null)
    // ⚠️ 不能让它自动清色：这块矩形里已经是合成好的画面，清了就是一个黑洞。
    // 只清深度（模型要重新做遮挡），颜色靠 backdrop 那张半透明板自己叠上去。
    const autoClear = renderer.autoClear
    renderer.autoClear = false
    renderer.setScissorTest(true)
    renderer.setScissor(vx, vy, vw, vh)
    renderer.setViewport(vx, vy, vw, vh)
    renderer.clearDepth()
    renderer.render(scene, camera)
    renderer.setScissorTest(false)
    renderer.setViewport(0, 0, cr.width, cr.height)
    renderer.autoClear = autoClear
    return true
  }

  function state() {
    return {
      hasModel: !!model,
      slot: !!slotEl,
      span: +modelSpan.toFixed(2),
      height: +modelHeight.toFixed(2),
      spinDeg: +((spin.rotation.z * 180) / Math.PI).toFixed(1),
      elevDeg: +(((CAM_ELEV + elevOffset) * 180) / Math.PI).toFixed(1),
      vel: +orbitVel.toFixed(3),
      dragging: dragging,
      scale: +spin.scale.x.toFixed(3),
      // 模型当前离槽位中心有多远（归一化单位），0 = 已经到位
      slideOff: +Math.hypot(spin.position.x, spin.position.y).toFixed(2),
    }
  }

  function dispose() {
    disposed = true
    disposeModel()
    glow.geometry.dispose()
    ring.geometry.dispose()
    backdrop.geometry.dispose()
    backdrop.material.dispose()
    glow.material.map && glow.material.map.dispose()
    glow.material.dispose()
    ring.material.dispose()
  }

  return {
    show,
    hide,
    setSlot,
    render,
    state,
    dispose,
    beginOrbit,
    orbitBy,
    endOrbit,
    get active() {
      return !!model
    },
    /** 手在拖、或惯性还没停：这段时间要按全速出帧，不能限到 30fps */
    get interacting() {
      return dragging || Math.abs(orbitVel) > 0.02
    },
  }
}
